import React, { useEffect } from 'react';
import { lockBodyScroll } from '../utils/bodyScrollLock';

export function FormSection({ title, icon, children, id }) {
  return (
    <section className="vi-form-section" aria-labelledby={id}>
      <h3 id={id} className="vi-form-section__title">
        {icon && <span className="vi-form-section__icon" aria-hidden>{icon}</span>}
        {title}
      </h3>
      <div className="vi-form-grid">{children}</div>
    </section>
  );
}

export function FormField({ children, full, hint }) {
  return (
    <div className={`vi-form-field${full ? ' vi-form-field--full' : ''}`}>
      {children}
      {hint && <p className="vi-form-hint">{hint}</p>}
    </div>
  );
}

/**
 * Modal form shell.
 * @param {boolean} fixedSize — lock width/height so tab switches do not resize the window
 * @param {React.ReactNode} toolbar — sticky strip under the header (e.g. HubTabs)
 */
export default function FormDialog({
  open,
  onClose,
  title,
  subtitle,
  icon = '✨',
  maxWidth = 720,
  fixedSize = false,
  height,
  loading = false,
  footer,
  toolbar,
  children,
  className = '',
}) {
  useEffect(() => {
    if (!open) return undefined;
    return lockBodyScroll();
  }, [open]);

  if (!open) return null;

  function handleBackdrop(e) {
    if (e.target === e.currentTarget && !loading) onClose?.();
  }

  const panelClass = [
    'vi-form-dialog',
    fixedSize ? 'vi-form-dialog--fixed' : '',
    toolbar ? 'vi-form-dialog--has-toolbar' : '',
    className,
  ].filter(Boolean).join(' ');

  const panelStyle = {
    maxWidth,
    ...(fixedSize ? { width: '100%' } : {}),
    ...(height ? { height, maxHeight: height } : {}),
  };

  return (
    <div
      className={`vi-form-dialog-overlay${fixedSize ? ' vi-form-dialog-overlay--fixed' : ''}`}
      onClick={handleBackdrop}
      role="presentation"
    >
      <div
        className={panelClass}
        style={panelStyle}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="vi-form-dialog-title"
      >
        <header className="vi-form-dialog__header">
          <div className="vi-form-dialog__header-inner">
            {icon && <span className="vi-form-dialog__header-icon" aria-hidden>{icon}</span>}
            <div className="vi-form-dialog__header-text">
              <h2 id="vi-form-dialog-title" className="vi-form-dialog__title">{title}</h2>
              {subtitle && <p className="vi-form-dialog__subtitle">{subtitle}</p>}
            </div>
          </div>
          {!loading && (
            <button
              type="button"
              className="vi-form-dialog__close"
              onClick={onClose}
              aria-label="إغلاق"
            >
              ✕
            </button>
          )}
        </header>

        {toolbar && (
          <div className="vi-form-dialog__toolbar">
            {toolbar}
          </div>
        )}

        <div className="vi-form-dialog__body">{children}</div>

        {footer && (
          <footer className="vi-form-dialog__footer">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

export function DialogButton({ variant = 'primary', type = 'button', disabled, loading, onClick, children, form, ...rest }) {
  return (
    <button
      type={type}
      form={form}
      className={`vi-dialog-btn vi-dialog-btn--${variant}`}
      disabled={disabled || loading}
      onClick={onClick}
      {...rest}
    >
      {loading ? 'جاري الحفظ…' : children}
    </button>
  );
}
